<template>
  <h2 class="text-lg font-bold text-left">Jornada de trabalho</h2>

  <div class="flex flex-col m-auto ml-0 rounded-lg overflow-hidden bg-[#F0F0F4] mb-6">
    <div class="px-6 py-4 rounded-t-lg" :class="originalContainerClasses">
      <div class="flex items-start gap-2">
        <div class="flex flex-col gap-2 w-full">
          <div class="flex justify-between font-bold text-center" :class="originalTextClasses">
            <span class="w-1/2">Início</span>
            <span class="w-1/2">Fim</span>
          </div>
          <div class="flex justify-between items-center">
            <SVGIcon :path="originalIcon" class="w-4 h-4 mt-1" :class="originalIconClasses" />

            <div class="flex gap-8 ml-3">
              <div class="flex gap-2">
                <DateTimeDisplay
                  :date="formatDutyJourney(originalStartHour)?.date"
                  :time="formatDutyJourney(originalStartHour)?.time"
                  :text-class="originalTextClasses"
                />
              </div>

              <div class="flex gap-2">
                <DateTimeDisplay
                  :date="formatDutyJourney(originalEndHour)?.date"
                  :time="formatDutyJourney(originalEndHour)?.time"
                  :text-class="originalTextClasses"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="px-6 py-4 rounded-b-lg" :class="requestedContainerClasses">
      <div class="flex items-start gap-2">
        <div class="flex flex-col gap-2 w-full">
          <div class="flex justify-between font-bold text-center" :class="requestedTextClasses">
            <span class="w-1/2">Início</span>
            <span class="w-1/2">Fim</span>
          </div>

          <div class="flex justify-between items-center">
            <SVGIcon
              v-if="approvalStatus !== 'Rejected'"
              :path="mdiLockOpenVariant"
              class="w-4 h-4 mt-1"
              :class="requestedIconClasses"
            />
            <div v-else class="w-4 h-4 mt-1"></div>

            <div class="flex gap-8 ml-3">
              <div class="flex gap-2">
                <DateTimeDisplay
                  :date="formatDutyJourney(requestedStartHour)?.date"
                  :time="formatDutyJourney(requestedStartHour)?.time"
                  :text-class="requestedTextClasses"
                />
              </div>

              <div class="flex gap-2">
                <DateTimeDisplay
                  :date="formatDutyJourney(requestedEndHour)?.date"
                  :time="formatDutyJourney(requestedEndHour)?.time"
                  :text-class="requestedTextClasses"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SVGIcon from '@/components/SVGIcon.vue'
import DateTimeDisplay from './DateTimeDisplay.vue'
import { mdiMinusCircleOutline, mdiLockOpenVariant, mdiLock } from '@mdi/js'

type ApprovalStatus = 'Approved' | 'Rejected' | null

interface FormattedDateTime {
  weekday: string
  date: string
  time: string
}

const props = defineProps<{
  originalStartHour: string
  originalEndHour: string
  requestedStartHour: string
  requestedEndHour: string
  timezone: string | null | undefined
  approvalStatus?: ApprovalStatus
}>()

function formatDate(dateString?: string, timezone: string = '-03:00'): FormattedDateTime | null {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: timezone })
    const formattedDate = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timezone
    })
    const formattedTime = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    })
    return {
      weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
      date: formattedDate,
      time: formattedTime
    }
  } catch {
    return null
  }
}

const formatDutyJourney = (dateString?: string): FormattedDateTime | null => {
  return formatDate(dateString, props.timezone || '-03:00')
}

const BG_NEUTRAL = 'bg-[#F0F0F4]'
const TEXT_MUTED = 'text-gray-400'

const originalContainerClasses = computed(() => {
  if (props.approvalStatus === 'Approved')
    return 'rounded-lg border-2 border-[#F0F0F4] bg-[#F0F0F4]'
  if (props.approvalStatus === 'Rejected')
    return 'rounded-lg border-2 border-primary30 bg-[#E2E2E9]'
  return BG_NEUTRAL
})

const originalTextClasses = computed(() => {
  return props.approvalStatus === 'Approved' ? TEXT_MUTED : 'text-[#212529]'
})

const originalIcon = computed(() => {
  return props.approvalStatus === 'Rejected' ? mdiLock : mdiMinusCircleOutline
})

const originalIconClasses = computed(() => {
  if (props.approvalStatus === 'Approved') return TEXT_MUTED
  if (props.approvalStatus === 'Rejected') return 'text-primary30'
  return 'text-negative'
})

const requestedContainerClasses = computed(() => {
  if (props.approvalStatus === 'Approved') return 'bg-[#FCE5E8]'
  if (props.approvalStatus === 'Rejected') return BG_NEUTRAL
  return 'bg-[#FCE5E8]'
})

const requestedTextClasses = computed(() => {
  return props.approvalStatus === 'Rejected' ? TEXT_MUTED : 'text-[#212529]'
})

const requestedIconClasses = computed(() => {
  if (props.approvalStatus === 'Rejected') return TEXT_MUTED
  return 'text-negative'
})
</script>
